import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import { useSession } from '../auth/session-context.js';
import { Alert } from '../components/Alert.js';
import { Field } from '../components/Field.js';
import { AuthLayout } from './AuthLayout.js';

/** Minimum enforced by the server; stated up front rather than after a rejection. */
const MIN_PASSWORD_LENGTH = 12;

/**
 * Registration and workspace onboarding (ARCHITECTURE.md §12).
 *
 * One step creates the account and the first workspace, matching the §12 flow.
 * No session is issued until the address is confirmed, so this ends on a
 * "check your email" state rather than dropping the user into the Console.
 */
export function SignupPage(): React.JSX.Element {
  const { status } = useSession();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  if (status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  const handleSubmit = async (event: React.SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await apiRequest('/auth/register', {
        method: 'POST',
        body: { email, password, displayName, workspaceName },
      });
      setRegistered(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setSubmitting(false);
    }
  };

  if (registered) {
    return (
      <AuthLayout
        title="Confirm your email"
        footer={
          <>
            Already confirmed? <Link to="/login">Sign in</Link>
          </>
        }
      >
        <Alert tone="success">
          We sent a confirmation link to <strong>{email}</strong>. Open it to finish setting up your
          Connaro account.
        </Alert>
        <p style={{ color: 'var(--c-text-muted)', fontSize: 'var(--text-sm)' }}>
          The link expires in 24 hours. You can <Link to="/verify-email">request a new one</Link> if
          it does not arrive.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="This also creates your first workspace. You can add more later."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      {/*
        * A closed door is not an error the person did anything to cause.
        *
        * `REGISTRATION_CLOSED` gets its own tone and hides the form, because
        * leaving fields under a red banner invites somebody to correct their
        * password and try again — and nothing they type will ever work. The
        * API is authoritative; this only renders what it said.
        */}
      {error?.code === 'REGISTRATION_CLOSED' ? (
        <Alert tone="info">{error.message}</Alert>
      ) : (
        error !== null && <Alert tone="error">{error.message}</Alert>
      )}

      {error?.code === 'REGISTRATION_CLOSED' ? null : (
      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        noValidate
      >
        <Field
          label="Your name"
          value={displayName}
          onChange={setDisplayName}
          autoComplete="name"
          required
          error={error?.fieldError('displayName')}
        />
        <Field
          label="Email address"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          error={error?.fieldError('email')}
        />
        <Field
          label="Workspace name"
          value={workspaceName}
          onChange={setWorkspaceName}
          hint="Usually your channel or team name."
          required
          error={error?.fieldError('workspaceName')}
        />
        <Field
          label="Password"
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          hint={`At least ${String(MIN_PASSWORD_LENGTH)} characters. Length matters more than symbols.`}
          error={
            passwordTooShort
              ? `Use at least ${String(MIN_PASSWORD_LENGTH)} characters.`
              : error?.fieldError('password')
          }
        />

        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={submitting || passwordTooShort}
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
      )}
    </AuthLayout>
  );
}
